import { ReactNode } from 'react'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import ComparisonTable from './comparison-table'
import { comparisonSchema, type CompareCell, type ComparisonSource } from './comparison-data'
export type { CompareCell } from './comparison-data'

export interface CompareRow {
  id?: string
  sources?: ComparisonSource[]
  feature: string
  detail?: string
  monofor: CompareCell
  competitor: CompareCell
}

export interface CompareFaq {
  q: string
  a: string
}

export interface Reason {
  title: string
  body: string
}

const ui = {
  trialNote: {
    en: 'Fully-loaded trial tenant in five minutes. No credit card, no sales gate.',
    tr: 'Be\u015f dakikada tam donan\u0131ml\u0131 deneme ortam\u0131. Kredi kart\u0131 yok, sat\u0131\u015f engeli yok.',
  },
  startTrial: { en: 'Start free trial \u2192', tr: '\u00dccretsiz deneme ba\u015flat \u2192' },
  talkExperts: { en: 'Talk with our experts', tr: 'Uzmanlar\u0131m\u0131zla g\u00f6r\u00fc\u015f\u00fcn' },
  sideBySide: { en: 'Side by side', tr: 'Yan yana' },
  faqEyebrow: { en: 'FAQ', tr: 'SSS' },
  faqTitle: { en: 'Common questions.', tr: 'S\u0131k sorulan sorular.' },
  noteA: {
    en: 'Comparison based on publicly available information as of July 2026.',
    tr: 'Kar\u015f\u0131la\u015ft\u0131rma, Temmuz 2026 itibar\u0131yla kamuya a\u00e7\u0131k bilgilere dayanmaktad\u0131r.',
  },
  noteB: {
    en: 'is a trademark of its respective owner; Monofor is not affiliated with or endorsed by it. Spotted something out of date?',
    tr: 'ilgili sahibinin ticari markas\u0131d\u0131r; Monofor\u2019un onunla ba\u011flant\u0131s\u0131 veya onay ili\u015fkisi yoktur. G\u00fcncelli\u011fini yitiren bir \u015fey mi g\u00f6rd\u00fcn\u00fcz?',
  },
  tellUs: { en: 'Tell us', tr: 'Bize yaz\u0131n' },
}

interface ComparePageProps {
  /** Optional gated-asset section rendered just before the final CTA. */
  leadMagnet?: React.ReactNode
  competitor: string
  /** Page locale; pass from getStaticProps for bilingual pages. */
  locale?: string
  /** When true, emits EN/TR hreflang alternates and locale-aware canonical. */
  bilingual?: boolean
  path: string
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: ReactNode
  heroBody: string
  reasons: Reason[]
  tableTitle: ReactNode
  tableBody: string
  rows: CompareRow[]
  comparisonNote?: string
  faqs: CompareFaq[]
}

export default function ComparePage({
  competitor,
  locale = 'en',
  bilingual = false,
  path,
  seoTitle,
  seoDescription,
  heroEyebrow,
  heroTitle,
  heroBody,
  reasons,
  tableTitle,
  tableBody,
  rows,
  faqs,
  leadMagnet,
  comparisonNote,
}: ComparePageProps) {
  const lang = locale === 'tr' ? 'tr' : 'en'
  const basePath = bilingual && lang === 'tr' ? '/tr' : ''
  const columns = [{ name: 'Monofor', highlight: true }, { name: competitor }]
  const entries = rows.map(row => ({ ...row, values: [row.monofor, row.competitor] }))
  const caption = lang === 'tr' ? `Monofor ve ${competitor}: özellik karşılaştırması` : `Monofor vs ${competitor}: capability comparison`
  return (
    <Layout>
      <Seo title={seoTitle} description={seoDescription} path={`${basePath}${path}`} />
      {bilingual && (
        <Head>
          <link rel="alternate" hrefLang="en" href={`${SITE_URL}${path}`} />
          <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr${path}`} />
          <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />
        </Head>
      )}
      <JsonLd id="comparison-page" data={comparisonSchema({ url: `${SITE_URL}${basePath}${path}`, title: seoTitle, description: seoDescription, caption, lang, columns, rows: entries })} />
      <JsonLd
        id="compare-faq"
        data={{
          '@type': 'FAQPage',
          '@id': `${SITE_URL}${basePath}${path}#faq`,
          inLanguage: lang,
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary" lang={lang}>
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <BackgroundField />
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{heroEyebrow}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {heroTitle}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {heroBody}
                </Text>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Button href="/try/now" variant="primary" size="lg">
                  {ui.startTrial[lang]}
                </Button>
                <Button href="/contact" variant="outline" size="lg">
                  {ui.talkExperts[lang]}
                </Button>
                <a href="#comparison" className="px-3 py-3 text-body-sm font-medium text-brand-700 underline underline-offset-4">
                  {lang === 'tr' ? 'Özellikleri karşılaştır ↓' : 'Compare capabilities ↓'}
                </a>
              </div>
              <div className="mt-6 text-body-sm text-ink-tertiary">
                {ui.trialNote[lang]}
              </div>
            </div>
          </Container>
        </Section>

        {/* Why teams switch */}
        <Section tone="subtle" density="default">
          <Container size="xl">
            <div className="grid gap-6 lg:grid-cols-3">
              {reasons.map((reason) => (
                <div
                  key={reason.title}
                  className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
                >
                  <h2 className="text-display-sm font-medium text-ink-primary">
                    {reason.title}
                  </h2>
                  <Text size="md" tone="secondary" className="mt-4">
                    {reason.body}
                  </Text>
                </div>
              ))}
            </div>
          </Container>
        </Section>

        {/* Comparison table */}
        <Section tone="default" density="default">
          <Container size="xl">
            <div className="max-w-3xl">
              <Eyebrow tone="accent">{ui.sideBySide[lang]}</Eyebrow>
              <Heading as="h2" size="display-lg" className="mt-5">
                {tableTitle}
              </Heading>
              <Text size="xl" tone="secondary" className="mt-6">
                {tableBody}
              </Text>
            </div>

            <ComparisonTable columns={columns} rows={entries} caption={caption} lang={lang} />

            <div className="mt-6 text-body-sm text-ink-tertiary">
              {comparisonNote || ui.noteA[lang]} {competitor} {ui.noteB[lang]}{' '}
              <a
                href="mailto:hello@monofor.com"
                className="underline underline-offset-4 hover:text-ink-secondary"
              >
                {ui.tellUs[lang]}
              </a>
              .
            </div>
          </Container>
        </Section>

        <TrustBand />

        {/* FAQ */}
        <Section id="faq" tone="subtle" density="default">
          <Container size="md">
            <Eyebrow tone="brand">{ui.faqEyebrow[lang]}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {ui.faqTitle[lang]}
            </Heading>
            <dl className="mt-12 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.q}
                  className="rounded-2xl border border-line-subtle bg-surface p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {faq.q}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {faq.a}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        {leadMagnet}
        <FinalCta />
      </main>
    </Layout>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
    </div>
  )
}
